import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';
import { getCatalogRamSnapshotCache } from '../../catalog/infra-api';
import {
  dismissOwnerChecklist,
  isOwnerChecklistDismissed,
} from './onboardingUiStorage';
import {
  readCachedDay1NonMiscProductCount,
  readDay1CatalogHydrated,
  readDay1NonMiscProductCount,
  writeCachedDay1NonMiscProductCount,
} from './day1PathEvidence';
import { day1NextCopy, day1StepLabels } from './day1PathCopy';
import { getHomeSnapshot } from '../../shell/home/homeSnapshotRepo';
import { resolveDay1Path, isDay1HomePending, type Day1PathResult } from './resolveDay1Path';
import { useDay1PathNavStore } from '../store/useDay1PathNavStore';
import { homeDbg, homeDbgTimed } from '../../shared/dev/homeDebug';
import { trackOnboardingFunnel } from './onboardingFunnel';
import { hapticSuccess } from '../../shared/ui/haptics';

type Options = {
  userId: string | undefined;
  /** Módulo Caja visible (capability cash_sessions + permiso). */
  cashModuleAllowed: boolean;
  cashSessionOpen: boolean;
  /** `getMergedActiveCashSession` ya resolvió (evita falso “Abrir caja” pre-load). */
  cashLoaded: boolean;
  /** Urgencias / ventas del día / pedidos: el local ya opera. */
  operationallyActive?: boolean;
};

export function useDay1Path({
  userId,
  cashModuleAllowed,
  cashSessionOpen,
  cashLoaded,
  operationallyActive = false,
}: Options) {
  const { entitlements, loading: entitlementsLoading } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const branchId = useSessionStore((s) => s.currentBranchId);
  const serviceCatalog = useHasBusinessCapability('service_catalog');
  // Capability-driven: con agenda ON el hito día 1 es el primer turno.
  const hasAppointments = useHasBusinessCapability('appointments');
  const setFromPath = useDay1PathNavStore((s) => s.setFromPath);
  const resetNav = useDay1PathNavStore((s) => s.reset);

  const [pathDismissed, setPathDismissed] = useState(false);
  const [dismissLoaded, setDismissLoaded] = useState(false);
  const [nonMiscProductCount, setNonMiscProductCount] = useState(0);
  const [lifetimeSaleCount, setLifetimeSaleCount] = useState(0);
  const [lifetimeAppointmentCount, setLifetimeAppointmentCount] = useState(0);
  const [catalogHydrated, setCatalogHydrated] = useState(false);
  const [storePublished, setStorePublished] = useState<boolean | null>(null);
  const [evidenceLoaded, setEvidenceLoaded] = useState(false);

  useEffect(() => {
    if (!userId) {
      setDismissLoaded(true);
      setPathDismissed(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const dismissed = await isOwnerChecklistDismissed(userId);
      if (!cancelled) {
        setPathDismissed(dismissed);
        setDismissLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const reloadEvidenceDebouncedRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const evidenceInFlightRef = useRef(false);
  const lastCatalogSyncedKeyRef = useRef<string | null>(null);
  const lastEvidenceReloadAtRef = useRef(0);
  const MIN_EVIDENCE_RELOAD_MS = 5000;

  const reloadEvidence = useCallback(async () => {
    if (evidenceInFlightRef.current) {
      homeDbg('useDay1Path', 'evidence skip — in flight');
      return;
    }
    if (!branchId) {
      setNonMiscProductCount(0);
      setLifetimeSaleCount(0);
      setLifetimeAppointmentCount(0);
      setCatalogHydrated(false);
      // null = desconocido; false forzaba el CTA “configurá tienda” sin evidencia.
      setStorePublished(null);
      setEvidenceLoaded(true);
      homeDbg('useDay1Path', 'evidence — no branch');
      return;
    }
    // No vaciar storePublished acá: al hacer focus (alt-tab) se revalida y un null
    // intermedio flippea el sidebar a “Tienda online” completa y después al CTA.
    homeDbg('useDay1Path', 'evidence → start', { branchId });
    evidenceInFlightRef.current = true;
    try {
      // lifetime_sales + storefront.is_public vienen del home_snapshot
      // compartido (TTL + in-flight dedupe con el load de Home y los providers).
      const [products, hydrated, snap, cachedCount] = await homeDbgTimed(
        'useDay1Path',
        'evidence',
        () =>
          Promise.all([
            readDay1NonMiscProductCount(),
            readDay1CatalogHydrated(),
            getHomeSnapshot(branchId),
            readCachedDay1NonMiscProductCount(branchId),
          ]),
      );
      const sales = snap?.lifetimeSales ?? 0;
      const appointments = snap?.lifetimeAppointments ?? 0;
      const published = snap?.storefront?.isPublic;
      // Sin RAM: usar último conteo cacheado para no flash “Cargá productos” → “Abrí la caja”.
      const effectiveCount = hydrated ? products : (cachedCount ?? products);
      const effectiveHydrated = hydrated || cachedCount != null;
      if (hydrated) {
        void writeCachedDay1NonMiscProductCount(branchId, products);
      }
      setNonMiscProductCount(effectiveCount);
      setLifetimeSaleCount(sales);
      setLifetimeAppointmentCount(appointments);
      setCatalogHydrated(effectiveHydrated);
      const publishedBool = published === true;
      setStorePublished(publishedBool);
      useDay1PathNavStore.getState().setStorePublished(publishedBool);
      homeDbg('useDay1Path', 'evidence → ok', {
        products,
        sales,
        hydrated,
        cachedCount,
        effectiveCount,
        effectiveHydrated,
        published,
      });
    } catch (e) {
      homeDbg('useDay1Path', 'evidence → fail', {
        error: e instanceof Error ? e.message : String(e),
      });
      setNonMiscProductCount(0);
      setLifetimeSaleCount(0);
      setLifetimeAppointmentCount(0);
      setCatalogHydrated(false);
      setStorePublished((prev) => {
        const next = prev === null ? false : prev;
        useDay1PathNavStore.getState().setStorePublished(next);
        return next;
      });
    } finally {
      evidenceInFlightRef.current = false;
      setEvidenceLoaded(true);
    }
  }, [branchId]);

  useEffect(() => {
    setEvidenceLoaded(false);
    // Sucursal / identity cambió: ocultar zona Tienda hasta la nueva evidencia.
    resetNav();
    void reloadEvidence();
  }, [reloadEvidence, resetNav]);

  useEffect(() => {
    return subscribeCatalogSynced((payload) => {
      const key = `${payload.productsCount}:${payload.categoriesCount}:${payload.suppliersCount}:${payload.brandsCount}`;
      if (lastCatalogSyncedKeyRef.current === key) {
        homeDbg('useDay1Path', 'catalogSynced skip — same payload');
        return;
      }
      lastCatalogSyncedKeyRef.current = key;

      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      if (now - lastEvidenceReloadAtRef.current < MIN_EVIDENCE_RELOAD_MS) {
        homeDbg('useDay1Path', 'catalogSynced skip — min interval');
        return;
      }

      homeDbg('useDay1Path', 'catalogSynced — debounce evidence', { key });
      if (reloadEvidenceDebouncedRef.current) {
        clearTimeout(reloadEvidenceDebouncedRef.current);
      }
      reloadEvidenceDebouncedRef.current = setTimeout(() => {
        reloadEvidenceDebouncedRef.current = null;
        lastEvidenceReloadAtRef.current =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
        void reloadEvidence();
      }, 300);
    });
  }, [reloadEvidence]);

  useEffect(() => {
    return () => {
      if (reloadEvidenceDebouncedRef.current) {
        clearTimeout(reloadEvidenceDebouncedRef.current);
      }
    };
  }, []);

  // Poll: cuando el sync llena la RAM, actualizar conteo/fase (sin bloquear paint).
  useEffect(() => {
    if (catalogHydrated) return;
    if (!evidenceLoaded) return;
    let cancelled = false;
    const applyCache = () => {
      const cache = getCatalogRamSnapshotCache();
      if (!cache) return false;
      const products = cache.products.filter(
        (p) => Number(p.is_misc ?? 0) === 0 && Number(p.is_active ?? 1) === 1,
      ).length;
      setCatalogHydrated(true);
      setNonMiscProductCount(products);
      if (branchId) void writeCachedDay1NonMiscProductCount(branchId, products);
      homeDbg('useDay1Path', 'catalog RAM detected via poll', { products });
      return true;
    };
    if (applyCache()) return;
    const id = setInterval(() => {
      if (cancelled) return;
      if (applyCache()) clearInterval(id);
    }, 250);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [catalogHydrated, evidenceLoaded, branchId]);

  const result: Day1PathResult | null = useMemo(() => {
    if (
      isDay1HomePending({
        entitlementsLoading,
        dismissLoaded,
        evidenceLoaded,
        cashBlocking: cashModuleAllowed && !cashLoaded,
        storePublished,
        hasBranch: Boolean(branchId),
        isOwner,
        pathDismissed,
        catalogHydrated,
        lifetimeSaleCount,
        operationallyActive,
      })
    ) {
      return null;
    }
    return resolveDay1Path({
      isOwner,
      pathDismissed,
      catalogHydrated,
      nonMiscProductCount,
      needsCashStep: cashModuleAllowed,
      cashSessionOpen,
      lifetimeSaleCount,
      appointmentsEnabled: hasAppointments,
      lifetimeAppointmentCount,
      storePublished,
      operationallyActive,
    });
  }, [
    entitlementsLoading,
    dismissLoaded,
    evidenceLoaded,
    cashModuleAllowed,
    cashLoaded,
    branchId,
    isOwner,
    pathDismissed,
    catalogHydrated,
    nonMiscProductCount,
    cashSessionOpen,
    lifetimeSaleCount,
    hasAppointments,
    lifetimeAppointmentCount,
    storePublished,
    operationallyActive,
  ]);

  // Funnel (#1362): una entrada por branch+fase en la sesión.
  const lastReportedPhaseRef = useRef<string | null>(null);

  useEffect(() => {
    if (!result) {
      // No resetear acá: resetNav en reloadEvidence (cambio de sucursal) ya pone loaded=false.
      // Si reseteamos en cada null (cash / evidence en vuelo), el sidebar flashea nav completa.
      return;
    }
    const funnelKey = `${branchId ?? 'no-branch'}:${result.phase}`;
    if (
      result.mode === 'fresh' &&
      result.phase !== 'done' &&
      lastReportedPhaseRef.current !== funnelKey
    ) {
      lastReportedPhaseRef.current = funnelKey;
      trackOnboardingFunnel('day1_phase_entered', {
        phase: result.phase,
        mode: result.mode,
        milestone: result.milestone,
      });
      // Primer hito = payoff del funnel: haptic de éxito una sola vez por
      // branch+sesión (el ref dedup también evita refire por remount de Home).
      if (
        result.phase === 'celebrate_first_sale' ||
        result.phase === 'celebrate_first_appointment'
      ) {
        hapticSuccess();
      }
    }
    homeDbg('useDay1Path', 'setFromPath', {
      mode: result.mode,
      phase: result.phase,
      preferCatalogTab: result.preferCatalogTab,
      collapseStorefrontNav: result.collapseStorefrontNav,
    });
    setFromPath({
      mode: result.mode,
      phase: result.phase,
      preferCatalogTab: result.preferCatalogTab,
      collapseStorefrontNav: result.collapseStorefrontNav,
    });
  }, [result, setFromPath, branchId]);

  const dismissPath = useCallback(async () => {
    if (!userId) return;
    await dismissOwnerChecklist(userId);
    setPathDismissed(true);
  }, [userId]);

  const copy = useMemo(() => {
    if (!result || result.phase === 'done' || result.awaitingCatalog) return null;
    return day1NextCopy({
      phase: result.phase,
      stepIndex: result.stepIndex,
      stepTotal: result.stepTotal,
      serviceCatalog,
      milestone: result.milestone,
      includesCashStep: cashModuleAllowed && result.milestone === 'sale',
    });
  }, [result, serviceCatalog, cashModuleAllowed]);

  const stepLabels = useMemo(() => day1StepLabels(serviceCatalog), [serviceCatalog]);

  const skipHomeHeavyRemoteLoad = useMemo(() => {
    // Gatear solo hasta que el path resuelve: si el pull de catálogo está en
    // cooldown esta sesión, la RAM nunca hidrata y `catalogHydrated` queda
    // false para siempre — una org operando/done perdería las cards de
    // urgencia (stock bajo, compras por recibir) en Home.
    if (!evidenceLoaded || !result) return true;
    if (result.mode === 'fresh') return true;
    return (
      result.phase === 'need_catalog' ||
      result.phase === 'need_cash' ||
      result.phase === 'need_first_sale' ||
      result.phase === 'need_first_appointment'
    );
  }, [evidenceLoaded, result]);

  return {
    isOwner,
    loaded: Boolean(result),
    result,
    copy,
    stepLabels,
    dismissPath,
    reloadEvidence,
    skipHomeHeavyRemoteLoad,
  };
}
